// src/pages/AIChatPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { aiAPI, companiesAPI } from '../services/api';
import { Card, SectionHeader, Button, ConfirmModal } from '../components/ui';
import ReactMarkdown from 'react-markdown';
import { BrainCircuit, Send, Trash2, Lightbulb } from 'lucide-react';
import styles from './AIChatPage.module.css';

const SUGGESTED_QUESTIONS = [
    '¿Cuánto inventario tenemos y para cuántos días nos alcanza?',
    'How much inventory do we have by product?',
    'Are there any stock anomalies I should know about?',
    '¿Qué productos están cerca del límite mínimo de stock?',
    'Based on current inventory, what can we produce this week?',
    'Analyze our consumption rate for the last 30 days.',
];

export default function AIChatPage() {
    const { companyId: jwtCompanyId, isGaafAdmin } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    // For GAAF admin: load companies list
    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = (res.data.companies || []).filter(c => c.status == null || c.status === 'active');
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const companyId = isGaafAdmin ? selectedCompanyId : jwtCompanyId;
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const sendMessage = async (text) => {
        const msg = text || input.trim();
        if (!msg || loading) return;
        setInput('');

        const userMsg = { role: 'user', content: msg, ts: Date.now() };
        setMessages(prev => [...prev, userMsg]);
        setLoading(true);

        try {
            const history = messages.map(m => ({ role: m.role, content: m.content }));
            const payload = {
                message: msg,
                conversationHistory: history,
                ...(companyId ? { companyId } : {}),
            };
            const res = await aiAPI.chat(payload);
            const assistantMsg = {
                role: 'assistant',
                content: res.data.reply,
                ts: Date.now(),
                tokens: res.data.tokensUsed,
            };
            setMessages(prev => [...prev, assistantMsg]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                ts: Date.now(),
                error: true,
            }]);
        }
        setLoading(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        if (messages.length === 0) return;
        setConfirmClear(true);
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="GAAF AI Inventory Assistant"
                description="Ask Questions about your Inventory in any language"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select
                                value={selectedCompanyId}
                                onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}
                            >
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        {messages.length > 0 && (
                            <Button variant="ghost" size="sm" icon={<Trash2 size={14} />} onClick={clearChat}>
                                Clear
                            </Button>
                        )}
                    </div>
                }
            />

            <div className={styles.chatWrap}>
                {/* Messages area */}
                <div className={styles.messages}>
                    {messages.length === 0 ? (
                        <div className={styles.emptyChat}>
                            <div className={styles.emptyChatIcon}>
                                <BrainCircuit size={32} />
                            </div>
                            <h3>Your GAAF AI Inventory Assistant</h3>
                            <p>Ask anything about your Inventory — Stock Levels, Forecasts, Anomaly Detection, Production Readiness. Responds in your language.</p>

                            <div className={styles.suggestions}>
                                <div className={styles.suggestionsTitle}>
                                    <Lightbulb size={13} /> Suggested Questions
                                </div>
                                <div className={styles.suggestionsList}>
                                    {SUGGESTED_QUESTIONS.map((q, i) => (
                                        <button key={i} className={styles.suggestion} onClick={() => sendMessage(q)}>
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, i) => (
                                <ChatMessage key={i} message={msg} />
                            ))}
                            {loading && <TypingIndicator />}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Input */}
                <div className={styles.inputArea}>
                    <textarea
                        ref={textareaRef}
                        className={styles.chatInput}
                        placeholder="Ask about Inventory, Forecasts, Anomalies… (Enter to Send, Shift+Enter for New Line)"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        disabled={loading}
                    />
                    <button
                        className={styles.sendBtn}
                        onClick={() => sendMessage()}
                        disabled={!input.trim() || loading}
                    >
                        <Send size={17} />
                    </button>
                </div>
            </div>

            <ConfirmModal
                open={confirmClear}
                onClose={() => setConfirmClear(false)}
                onConfirm={() => { setMessages([]); setConfirmClear(false); }}
                title="Clear Conversation"
                message="Clear the entire conversation history? This cannot be undone."
                confirmLabel="Clear"
                confirmVariant="danger"
            />
        </div>
    );
}

function ChatMessage({ message }) {
    const isUser = message.role === 'user';
    return (
        <div className={`${styles.messageRow} ${isUser ? styles.messageRowUser : styles.messageRowAssistant}`}>
            {!isUser && (
                <div className={styles.avatar}>
                    <BrainCircuit size={14} />
                </div>
            )}
            <div className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleAssistant} ${message.error ? styles.bubbleError : ''}`}>
                {isUser ? (
                    <p style={{ margin: 0, color: '#fff', fontSize: '0.9rem', lineHeight: 1.6 }}>{message.content}</p>
                ) : (
                    <div className={styles.markdown}>
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                )}
                {message.tokens && (
                    <div className={styles.tokenCount}>{message.tokens} tokens</div>
                )}
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
            <div className={styles.avatar}><BrainCircuit size={14} /></div>
            <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
                <div className={styles.typing}>
                    <span /><span /><span />
                </div>
            </div>
        </div>
    );
}
